/**
 * Project ↔ API workspace binding — the pure part (card 1.3, project scope).
 *
 * A project holds ANY NUMBER of API workspaces (owner, 21.10.2026):
 * `apiWorkspaces: [{ id, storage }]`, in the order they were added. `id` is the
 * workspace's own id (a UUID or a server int, never a records bucket) and
 * `storage` how it is stored, so the switcher can show the git / local / cloud
 * badge before the workspace list has loaded.
 *
 * `apiWorkspaceId` / `apiWorkspaceStorage` stay on the record as the FIRST
 * entry: app 1.0.6 reads only those, and a record written by it (no list yet)
 * is read here as a one-entry list.
 *
 * Two id spaces meet here and must not be confused: a team PROJECT lives in
 * the `team:<teamId>` records bucket, while a WORKSPACE is shared with zero or
 * more teams (`workspace.teams[]` = `{ id, name, is_primary }`). A team project
 * may only bind a workspace shared with THAT team — otherwise teammates would
 * see a project pointing at a workspace they cannot open.
 *
 * No Tauri, no store: projectsStore imports this to enumerate the fields.
 */
export const WORKSPACE_STORAGES = Object.freeze(['local', 'git', 'cloud']);

const MAX_WORKSPACES = 100;

function entryOf(raw) {
  const id = raw?.id == null ? '' : String(raw.id).trim();
  if (!id) return null;
  return { id: id.slice(0, 200), storage: WORKSPACE_STORAGES.includes(raw.storage) ? raw.storage : null };
}

/** The binding fields, normalised: the list, plus its first entry in the two legacy fields. */
export function normalizeBinding(draft = {}) {
  const raw = Array.isArray(draft.apiWorkspaces) && draft.apiWorkspaces.length
    ? draft.apiWorkspaces
    : [{ id: draft.apiWorkspaceId, storage: draft.apiWorkspaceStorage }];
  const list = [];
  for (const r of raw) {
    const e = entryOf(r);
    if (e && !list.some((x) => x.id === e.id) && list.length < MAX_WORKSPACES) list.push(e);
  }
  return {
    apiWorkspaceId: list[0]?.id ?? null,
    apiWorkspaceStorage: list[0]?.storage ?? null,
    apiWorkspaces: list,
  };
}

/** The ids of the API workspaces `project` holds, in order. */
export const workspaceIdsOf = (project) => normalizeBinding(project || {}).apiWorkspaces.map((w) => w.id);

/** Does `project` hold the workspace `workspaceId`? */
export const holdsWorkspace = (project, workspaceId) => workspaceId != null && workspaceIdsOf(project).includes(String(workspaceId));

/** The binding fields after adding `workspace` (no duplicate; appended). */
export function withWorkspace(project, workspace) {
  const list = normalizeBinding(project || {}).apiWorkspaces;
  const e = entryOf({ id: workspace?.id, storage: storageOfWorkspace(workspace) });
  return normalizeBinding({ apiWorkspaces: e && !list.some((x) => x.id === e.id) ? [...list, e] : list });
}

/** The binding fields after removing `workspaceId` (all of them when it is null). */
export function withoutWorkspace(project, workspaceId) {
  if (workspaceId == null) return normalizeBinding({ apiWorkspaces: [] });
  const list = normalizeBinding(project || {}).apiWorkspaces.filter((w) => w.id !== String(workspaceId));
  return normalizeBinding({ apiWorkspaces: list });
}

/** How a workspace object from workspaceStore is stored. */
export function storageOfWorkspace(workspace) {
  if (workspace?.storage === 'git') return 'git';
  if (workspace?.storage === 'local') return 'local';
  return 'cloud';
}

/** The binding fields of a project holding exactly `workspace` (a new or a first binding). */
export function bindingOf(workspace) {
  if (workspace?.id == null) return normalizeBinding({});
  return normalizeBinding({ apiWorkspaces: [{ id: workspace.id, storage: storageOfWorkspace(workspace) }] });
}

/** Is `workspace` shared with team `teamId`? Its `teams[]`, or its primary `team` / `team_id`. */
export function workspaceSharedWithTeam(workspace, teamId) {
  if (!workspace || teamId == null) return false;
  const want = String(teamId);
  const listed = Array.isArray(workspace.teams) && workspace.teams.some((t) => t != null && String(t.id ?? t) === want);
  return listed || String(workspace.team?.id ?? workspace.team_id ?? '') === want;
}

/**
 * Why a project in team `teamId` (null = personal) may NOT bind `workspace`,
 * as a `projects.error.<code>` code — or null when it may.
 */
export function bindingRefusal(teamId, workspace) {
  if (workspace?.id == null || String(workspace.id).trim() === '') return 'noWorkspace';
  if (teamId == null) return null;
  return workspaceSharedWithTeam(workspace, teamId) ? null : 'teamWorkspaceNotShared';
}
